import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { fmtDate, fmtTime } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import * as Icons from "lucide-react";

const Stat = ({ label, value, tone = "text-slate-800", tid }) => (
  <div className="bg-white rounded-xl border border-slate-200 px-4 py-3" data-testid={tid}>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-xl font-semibold mt-0.5 tabular-nums ${tone}`}>{value}</div>
  </div>
);

const hm = (iso) => (iso ? fmtTime(iso) : "");
const hrs = (n) => `${Math.round((Number(n) || 0) * 100) / 100} hrs`;
const work = (n) => { const v = Number(n) || 0; const h = Math.floor(v); const m = Math.round((v - h) * 60); return `${h} hrs. ${m} mins. (${Math.round(v * 100) / 100})`; };
const mdy = (d) => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : "");
const DAY = { Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday", Sun: "Sunday" };
const COLS = [
  ["User ID", (r) => r.user_id], ["Name", (r) => r.name], ["Schedule", (r) => r.schedule_label], ["Date", (r) => mdy(r.date)],
  ["Day", (r) => DAY[r.day] || r.day], ["Time In", (r) => hm(r.clock_in)], ["Time Out", (r) => hm(r.clock_out)],
  ["Work", (r) => (r.no_record ? "" : work(r.work_hours))], ["Lates", (r) => (r.no_record ? "" : hrs(r.late_hours))],
  ["Undertime", (r) => (r.no_record ? "" : hrs(r.undertime_hours))], ["Overtime", (r) => (r.no_record ? "" : hrs(r.overtime_hours))],
  ["Approved Overtime", (r) => (r.no_record ? "" : hrs(r.approved_overtime))], ["Night Diff", (r) => (r.no_record ? "" : hrs(r.night_diff))],
  ["OT Night Diff", (r) => (r.no_record ? "" : hrs(r.ot_night_diff))],
  ["Device", (r) => [r.site_in || r.clock_in ? `Time In : ${r.site_in || r.device || ""}` : "", r.site_out || r.clock_out ? `Time Out : ${r.site_out || r.device || ""}` : ""].filter(Boolean).join(" | ")],
  ["Remarks", (r) => [r.missing_in && r.missing_out ? "No Time In/Out" : r.missing_in ? "No Time In" : r.missing_out ? "No Time Out" : "", r.remarks].filter(Boolean).join(" · ")],
];

const exportCsv = (rows, s, start, end) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const total = ["Total", "", "", "", "", "", "", hrs(s.hours), hrs(s.late_hours), hrs(s.undertime), hrs(s.overtime), hrs(s.approved_overtime), hrs(s.night_diff), hrs(s.ot_night_diff), "", ""];
  const lines = [COLS.map(([h]) => h), ...rows.map((r) => COLS.map(([, f]) => f(r))), total].map((l) => l.map(esc).join(","));
  const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = `attendances_${start}_to_${end}.csv`; a.click(); URL.revokeObjectURL(a.href);
};

function SnapshotCell({ rec }) {
  const hasAny = rec.has_in_photo || rec.has_out_photo;
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [imgs, setImgs] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!hasAny || !ref.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) { setVisible(true); io.disconnect(); }
    }, { rootMargin: "120px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [hasAny]);

  useEffect(() => {
    if (visible && !imgs) {
      api.get(`/attendance/snapshot/${rec.id}`).then(({ data }) => setImgs(data)).catch(() => setImgs({ in: "", out: "" }));
    }
  }, [visible, imgs, rec.id]);

  if (!hasAny) return <span className="text-slate-300 text-xs">—</span>;
  const thumb = imgs?.in || imgs?.out;

  return (
    <div ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`att-photo-${rec.id}`}
        className="h-9 w-9 rounded-full overflow-hidden ring-1 ring-slate-200 hover:ring-blue-400 transition-all bg-slate-100 flex items-center justify-center"
        title="View clock-in / clock-out snapshot"
      >
        {thumb
          ? <img src={thumb} alt="attendance snapshot" className="h-full w-full object-cover" />
          : <Icons.Camera className="h-4 w-4 text-slate-400" />}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Attendance Snapshot · {fmtDate(rec.date)}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {["in", "out"].map((k) => (
              <div key={k} className="text-center">
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">{k === "in" ? "Clock In" : "Clock Out"}</div>
                {imgs?.[k]
                  ? <img src={imgs[k]} alt={`clock ${k}`} className="w-full aspect-square object-cover rounded-xl ring-1 ring-slate-200" data-testid={`att-photo-${k}-${rec.id}`} />
                  : <div className="w-full aspect-square rounded-xl bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center text-slate-300"><Icons.ImageOff className="h-6 w-6" /></div>}
                <div className="text-xs text-slate-500 mt-1.5 font-mono">{k === "in" ? hm(rec.clock_in) : hm(rec.clock_out)}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Attendances() {
  const [isHr, setIsHr] = useState(false);
  const [emps, setEmps] = useState([]);
  const [empId, setEmpId] = useState("");
  const today = new Date();
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const first = ymd(new Date(today.getFullYear(), today.getMonth(), 1));
  const [start, setStart] = useState(first);
  const [end, setEnd] = useState(ymd(today));
  const [data, setData] = useState({ records: [], summary: {} });

  const load = useCallback(() => {
    const qp = new URLSearchParams();
    if (empId) qp.set("employee_id", empId);
    if (start) qp.set("start", start);
    if (end) qp.set("end", end);
    api.get(`/attendance/log?${qp.toString()}`).then(({ data }) => { setData(data); setIsHr(!!data.is_hr); }).catch((e) => { setData({ records: [], summary: {} }); toast.error(e.response?.data?.detail || "Failed to load attendances"); });
  }, [empId, start, end]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [empId]);
  useEffect(() => { api.get("/employees?").then(({ data }) => setEmps(data)).catch(() => {}); }, []);

  const s = data.summary || {};
  const th = "text-[11px] font-semibold text-slate-600 whitespace-nowrap px-2";
  const td = "text-xs px-2 py-2 whitespace-nowrap tabular-nums";
  return (
    <div className="uemp-fade-in">
      <PageHeader title="Attendances" subtitle="Daily time logs — time in/out, work, lates, undertime, overtime and night differential." />

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="attendances-filters">
        {isHr && (
          <div className="space-y-1.5">
            <div className="text-xs text-slate-500">Employee</div>
            <Select value={empId || "__all__"} onValueChange={(v) => setEmpId(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-64" data-testid="attendances-employee"><SelectValue placeholder="All employees" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All employees</SelectItem>
                {emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_no}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5"><div className="text-xs text-slate-500">From</div><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="attendances-start" /></div>
        <div className="space-y-1.5"><div className="text-xs text-slate-500">To</div><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="attendances-end" /></div>
        <Button onClick={load} className="bg-indigo-700 hover:bg-indigo-800 h-10" data-testid="attendances-run">Submit</Button>
        <Button variant="outline" onClick={() => exportCsv(data.records, s, data.start || start, data.end || end)} disabled={!data.records.length} className="h-10 sm:ml-auto" data-testid="attendances-export-csv"><Icons.Download className="h-4 w-4 mr-1.5" />Export CSV</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3 mb-5">
        <Stat label="Total Work" value={hrs(s.hours)} tid="att-total-work" />
        <Stat label="Lates" value={hrs(s.late_hours)} tone="text-rose-600" tid="att-total-lates" />
        <Stat label="Undertime" value={hrs(s.undertime)} tone="text-amber-600" tid="att-total-undertime" />
        <Stat label="Overtime" value={hrs(s.overtime)} tone="text-blue-600" tid="att-total-overtime" />
        <Stat label="Approved OT" value={hrs(s.approved_overtime)} tone="text-indigo-600" tid="att-total-approved-ot" />
        <Stat label="Night Diff" value={hrs(s.night_diff)} tone="text-violet-600" tid="att-total-nd" />
        <Stat label="OT Night Diff" value={hrs(s.ot_night_diff)} tone="text-fuchsia-600" tid="att-total-ot-nd" />
        <Stat label="No Time In/Out" value={s.missing ?? 0} tone="text-red-600" tid="att-total-missing" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-auto max-h-[70vh]">
        <table className="w-full border-collapse" data-testid="attendances-grid">
          <thead className="sticky top-0 z-10 bg-slate-50">
            <tr className="border-b border-slate-200 text-left">
              {COLS.map(([h]) => <th key={h} className={`${th} py-2.5 border-r border-slate-100`}>{h}</th>)}
              <th className={`${th} py-2.5`}>Photo</th>
            </tr>
          </thead>
          <tbody data-testid="attendances-table">
            {data.records.length === 0 && <tr><td colSpan={COLS.length + 1} className="text-center text-slate-400 py-8 text-sm">No attendance records for this range.</td></tr>}
            {data.records.map((r) => {
              const miss = r.missing_in || r.missing_out;
              return (
                <tr key={r.id} className={`border-b border-slate-100 transition-colors hover:bg-slate-50/70 ${miss ? "bg-red-50/40" : ""}`} data-testid="attendances-row">
                  {COLS.map(([h, f]) => {
                    const v = f(r);
                    if (h === "Date") return <td key={h} className={`${td} font-medium ${miss ? "bg-red-500 text-white" : ""}`} title={miss ? (r.missing_in && r.missing_out ? "No time-in and time-out" : r.missing_in ? "No time-in" : "No time-out") : ""} data-testid={miss ? "att-missing-date" : undefined}>{v}</td>;
                    if (h === "Schedule") return <td key={h} className={`${td} text-[10px] text-slate-500 max-w-[180px] truncate`} title={v}>{v}</td>;
                    if (h === "Device") return <td key={h} className="text-[10px] px-2 py-2 text-slate-500 leading-4 min-w-[150px]">{v.split(" | ").map((x) => <div key={x}>{x}</div>)}</td>;
                    if (h === "Remarks") return <td key={h} className="text-xs px-2 py-2 text-slate-600 min-w-[140px]">{v}</td>;
                    if ((h === "Time In" && r.missing_in) || (h === "Time Out" && r.missing_out)) return <td key={h} className={td}><span className="text-[10px] font-semibold text-red-600">{r.no_record ? "" : "Missing"}</span></td>;
                    return <td key={h} className={`${td} ${h === "Name" ? "text-slate-800" : "text-slate-700"}`}>{v}</td>;
                  })}
                  <td className="px-2 py-1"><SnapshotCell rec={r} /></td>
                </tr>
              );
            })}
          </tbody>
          {data.records.length > 0 && (
            <tfoot className="sticky bottom-0 bg-slate-100 font-semibold" data-testid="attendances-total">
              <tr>
                <td className={td} colSpan={7}>Total</td>
                <td className={td}>{hrs(s.hours)}</td><td className={td}>{hrs(s.late_hours)}</td><td className={td}>{hrs(s.undertime)}</td>
                <td className={td}>{hrs(s.overtime)}</td><td className={td}>{hrs(s.approved_overtime)}</td><td className={td}>{hrs(s.night_diff)}</td>
                <td className={td}>{hrs(s.ot_night_diff)}</td><td className={td} colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
